/**
 * Thin helpers around Pixi 8's low-level GL objects: shader creation (GLSL ES 3.00 with highp
 * fragments — world coordinates reach ~6000 units, mediump would band the noise), raw data
 * textures and float16 packing for the smooth terrain fields.
 */
import {
  BufferImageSource,
  type Geometry,
  GlProgram,
  type Mesh,
  Shader,
  type TEXTURE_FORMATS,
  Texture,
} from 'pixi.js';

/** Standard vertex prelude: Pixi supplies these matrices for every Mesh. */
export const VERT_HEADER = /* glsl */ `#version 300 es
uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
vec4 toClip(vec2 world) {
  mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
  return vec4((mvp * vec3(world, 1.0)).xy, 0.0, 1.0);
}
`;

export const FRAG_HEADER = /* glsl */ `#version 300 es
precision highp float;
precision highp int;
out vec4 finalColor;
`;

export function makeShader(
  name: string,
  vertex: string,
  fragment: string,
  resources: Record<string, unknown>,
): Shader {
  return new Shader({
    glProgram: GlProgram.from({ name, vertex, fragment, preferredFragmentPrecision: 'highp' }),
    resources,
  });
}

/** Destroy a custom mesh together with its geometry buffers and shader (Mesh.destroy keeps both). */
export function destroyMesh(mesh: Mesh<Geometry, Shader>): void {
  const geometry = mesh.geometry;
  const shader = mesh.shader;
  mesh.destroy();
  geometry?.destroy(true);
  shader?.destroy();
}

export interface DataTextureOptions {
  width: number;
  height: number;
  data: Uint8Array | Uint16Array;
  format?: TEXTURE_FORMATS;
  linear?: boolean;
  repeat?: boolean;
  mipmaps?: boolean;
}

/**
 * A texture fed straight from a typed array. Alpha is NOT premultiplied on upload: every
 * channel is data, not colour.
 */
export function makeDataTexture(o: DataTextureOptions): Texture {
  const source = new BufferImageSource({
    resource: o.data,
    width: o.width,
    height: o.height,
    format: o.format ?? 'rgba8unorm',
    alphaMode: 'no-premultiply-alpha',
    scaleMode: o.linear ? 'linear' : 'nearest',
    addressMode: o.repeat ? 'repeat' : 'clamp-to-edge',
    autoGenerateMipmaps: !!o.mipmaps,
    mipmapFilter: 'linear',
  });
  return new Texture({ source });
}

const f32 = new Float32Array(1);
const u32 = new Uint32Array(f32.buffer);

/** IEEE-754 binary32 → binary16 (round-to-nearest, no NaN handling needed for our fields). */
export function toHalf(v: number): number {
  f32[0] = v;
  const x = u32[0];
  const sign = (x >>> 16) & 0x8000;
  let exp = ((x >>> 23) & 0xff) - 127 + 15;
  let mant = x & 0x7fffff;
  if (exp <= 0) {
    if (exp < -10) return sign;
    mant = (mant | 0x800000) >> (1 - exp);
    return sign | ((mant + 0x1000) >> 13);
  }
  if (exp >= 31) return sign | 0x7c00;
  mant += 0x1000;
  if (mant & 0x800000) {
    mant = 0;
    exp++;
    if (exp >= 31) return sign | 0x7c00;
  }
  return sign | (exp << 10) | (mant >> 13);
}
